import type { GeoPoint } from "../catalogue";
import { getJson, postEmpty, putJson } from "../client";
import { ENVOY_BASE } from "./base";

/** Who said where the envoy looks: the edge, from the browser, or the person. */
export type AreaSource = "EDGE" | "PERSON";

/** The distances a person can pick, in miles: the Jobs page's. `envoy_api::area::WITHIN_MILES`. */
export const AREA_WITHIN_MILES = [5, 25, 50] as const;

export type AreaWithinMiles = (typeof AREA_WITHIN_MILES)[number];

/** The distance the edge's guess is kept at. `envoy_api::area::DEFAULT_WITHIN_MILES`. */
export const DEFAULT_AREA_WITHIN_MILES: AreaWithinMiles = 25;

/**
 * Where a person's envoy looks for work they turn up to: one city, and how
 * far from it. `envoy_api::area::EnvoyArea`. Remote work goes by the
 * countries in the settings.
 */
export interface EnvoyArea {
    /** The city as a person reads it, such as `Denver, CO`. */
    city: string;
    /** ISO 3166-1 alpha-2, such as `US`. */
    country: string;
    /** ISO 3166-2, such as `US-CO`. Absent where it is not known. */
    region?: string | null;
    /** Where the city is: what the distance is measured from. */
    point: GeoPoint;
    /** 5, 25 or 50. */
    within_miles: number;
    by: AreaSource;
}

/** The area, or null until the edge or the person has said one. */
export interface EnvoyAreaAnswer {
    area?: EnvoyArea | null;
}

/**
 * The area a person says: a city from the catalogue's places, with its
 * point, and a distance. `envoy_api::area::PutEnvoyAreaRequest`.
 */
export interface EnvoyAreaRequest {
    city: string;
    country: string;
    /** Its own code (`CO`) or the whole one (`US-CO`). Null where it has none. */
    region?: string | null;
    point: GeoPoint;
    within_miles: AreaWithinMiles;
}

/** Where the caller's envoy looks. */
export function getEnvoyArea(): Promise<EnvoyAreaAnswer> {
    return getJson(`${ENVOY_BASE}/area`);
}

/** Saves the area the person says. From then on the edge does not move it. */
export function saveEnvoyArea(area: EnvoyAreaRequest): Promise<EnvoyAreaAnswer> {
    return putJson(`${ENVOY_BASE}/area`, area);
}

/**
 * Asks envoy to keep the city the edge says the browser is in, at 25
 * miles, unless the person said one. Answers the area as it stands.
 */
export function placeEnvoyAreaHere(): Promise<EnvoyAreaAnswer> {
    return postEmpty(`${ENVOY_BASE}/area/here`);
}
